import {useCartContext} from 'components/providers';
import {useDebouncedCartUpdate} from 'hooks';
import {useCallback} from 'react';
import {ICartItem, IProduct, IRestaurantResponse} from 'types';
import {v4 as uuidv4} from 'uuid';

interface UseCartProductProps {
  product: IProduct;
  provider?: IRestaurantResponse;
  onClick?: (p: IProduct) => void;
  onOpenAlertClearCart?: () => void;
  quantity: number;
  cartItemId?: string;
}

export const useMetroCart = ({product, provider, onOpenAlertClearCart, quantity, cartItemId}: UseCartProductProps) => {
  const {addProvider, cartProvider, clearCart, setCartItems} = useCartContext();

  // Use the debounced cart update hook
  const {
    localQuantity,
    isUpdating,
    isSelected,
    handleAdd: handleAddProduct,
    handleRemove: handleDelete,
  } = useDebouncedCartUpdate({
    product,
    provider,
    initialQuantity: quantity,
    cartItemId,
    onOpenAlertClearCart,
    debounceDelay: 300,
  });

  // Legacy fallback for clear cart functionality
  const handleClearAndAdd = useCallback(() => {
    clearCart(true);
    let productTracking = 'restaurant/';
    // Tracking product origin - added from carousel, crosseling, recommended
    const isFromReccomended = provider?.recommended_products.find((item) => item.id === product.id);
    productTracking += isFromReccomended ? 'restaurant_recommended' : 'restaurant';

    const newCartItem: ICartItem = {
      id: uuidv4(),
      product: {
        ...product,
        price: Number(product.price),
        discount: 0,
        discount_price: Number(product.price),
        mass_measurement: product.mass_measurement,
      },
      productId: product.id,
      price: Number(product.price),
      quantity: 1,
      instructions: '',
      selectedOptions: {},
      added_from: productTracking,
    };
    setCartItems([newCartItem]);
    if (provider) addProvider(provider);
  }, [clearCart, product, provider, setCartItems, addProvider]);

  return {
    handleAddProduct,
    handleDelete,
    cartProvider,
    addProvider,
    clearCart,
    setCartItems,
    handleClearAndAdd,
    // Use local quantity for immediate UI updates
    isSelected: isSelected || localQuantity > 0,
    localQuantity,
    isUpdating,
  };
};
